import type {ReactNode} from "react";
import {Fragment, useLayoutEffect, useRef, useState} from "react";

type TransitionFn<T> = (fn: (obj: T, closing: boolean) => ReactNode) => ReactNode[];

type TransMap<T> = Map<Key, {data: T; timeoutId: null | ReturnType<typeof setTimeout>; key: Key}>;

type Key = string | null | boolean;

export const useMyTransition = <T,>(
  obj: T,
  key: Key,
  opts?: {canRecover?: boolean; timeoutMs?: number}
): TransitionFn<T> => {
  const [map] = useState<TransMap<T>>(() => new Map());
  const [, forceUpdate] = useState<unknown>();
  const optsObj = {obj, canRecover: opts?.canRecover ?? true, timeoutMs: opts?.timeoutMs ?? 500};
  const objRef = useRef(optsObj);
  useLayoutEffect(() => {
    objRef.current = optsObj;
  });
  useLayoutEffect(() => {
    let found = false;
    let updated = false;
    // eslint-disable-next-line prefer-const
    for (let [elKey, val] of map) {
      if (elKey === key) {
        if (val.timeoutId) {
          clearTimeout(val.timeoutId);
          val.timeoutId = null;
          updated = true;
        }
        found = true;
      } else {
        if (!val.timeoutId) {
          if (!objRef.current.canRecover) {
            map.delete(elKey);
            elKey = `${key}-${new Date().getTime()}`;
            map.set(elKey, val);
          }
          val.timeoutId = setTimeout(() => {
            map.delete(elKey);
            forceUpdate({});
          }, objRef.current.timeoutMs);
          updated = true;
        }
      }
    }
    if (!found) {
      map.set(key, {data: objRef.current.obj, timeoutId: null, key});
      updated = true;
    }
    // re-renders before paint (layout effect), so the map change never shows up as an extra frame
    // eslint-disable-next-line react/set-state-in-effect
    if (updated) forceUpdate({});
  }, [key, map]);
  return (renderFn) =>
    [...map.values()].map((val) => (
      <Fragment key={`${val.key}`}>{renderFn(val.data, val.timeoutId !== null)}</Fragment>
    ));
};
